/**
 * Calculates the cosine similarity between two documents represented as strings.
 *
 * The function splits each input string into words, constructs word frequency vectors,
 * and computes the cosine similarity between these vectors.
 *
 * @param a - The first document as a string.
 * @param b - The second document as a string.
 * @param splitter - A regular expression or string used to split the documents into words. Defaults to whitespace.
 * @returns The cosine similarity between the two documents, ranging from 0 (no similarity) to 1 (identical).
 *
 * @example
 * ```typescript
 * const similarity = getDocumentSimilarity("hello world", "hello there");
 * // similarity will be a number between 0 and 1
 * ```
 */
export const getDocumentSimilarity = (a: string, b: string, splitter: RegExp|string = /\s+/): number => {
    const aWords = a.split(splitter);
    const bWords = b.split(splitter);

    const wordSet = new Set([...aWords, ...bWords]);

    const aVector = Array.from(wordSet).map(
      (word) => aWords.filter((w) => w === word).length
    );
    const bVector = Array.from(wordSet).map(
      (word) => bWords.filter((w) => w === word).length
    );

    const dotProduct = aVector.reduce(
      (sum, val, i) => sum + val * bVector[i],
      0
    );
    const magnitudeA = Math.sqrt(
      aVector.reduce((sum, val) => sum + val * val, 0)
    );
    const magnitudeB = Math.sqrt(
      bVector.reduce((sum, val) => sum + val * val, 0)
    );

    return magnitudeA && magnitudeB
      ? dotProduct / (magnitudeA * magnitudeB)
      : 0;
};